/**
 * Sending work to one's own agent: once, until it is done, or on a schedule.
 *
 * Used from the Work screen and from a document's right-click menu, where the document is already
 * chosen. Everything goes out as remote-control commands, so remote control has to be on; when it
 * is off this says so, with the way to switch it on, rather than letting the send fail.
 */

import SchedulePicker from './SchedulePicker'
import { DEFAULT_SCHEDULE, scheduleRequest, type ScheduleDraft } from '../lib/schedule'
import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarClock, Play, Repeat, Users } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { Modal } from './Modal'
import { TeamRolePicker, type TeamRoles } from './TeamRolePicker'
import { ErrorBanner } from './primitives'
import type { WorkOrder, WorkOrderKind } from '../lib/types'

const KNOWN_ERRORS = ['remote_off', 'not_linked', 'too_many_pending', 'invalid_interval', 'invalid_cron', 'invalid_timezone', 'too_often', 'too_long', 'required', 'not_found', 'plan_limit', 'plan_team_only', 'teammate_not_accepting', 'teammate_remote_off', 'not_in_team']

export function SendWorkDialog({
  kind: initialKind,
  docPath,
  onClose,
  onSent,
}: {
  kind: WorkOrderKind
  docPath?: string
  onClose: () => void
  onSent?: (order: WorkOrder) => void
}) {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const [kind, setKind] = useState<WorkOrderKind>(initialKind)
  const [instructions, setInstructions] = useState(docPath ? t('work.docInstructions') : '')
  const [repositoryId, setRepositoryId] = useState('')
  const [maxIterations, setMaxIterations] = useState(8)
  const [schedule, setSchedule] = useState<ScheduleDraft>(DEFAULT_SCHEDULE)
  const [roles, setRoles] = useState<TeamRoles>({ builder: null, reviewer: null })
  const [problem, setProblem] = useState<string | null>(null)

  const { data: remote } = useQuery({ queryKey: ['remote'], queryFn: api.remote.overview, retry: false })
  const { data: repositories } = useQuery({ queryKey: ['repositories'], queryFn: api.repositories.list })
  const linked = (repositories ?? []).filter((r) => r.linked)
  const remoteOff = remote !== undefined && !remote.enabled

  const send = useMutation({
    mutationFn: () =>
      api.workOrders.create({
        kind,
        instructions: instructions.trim(),
        repositoryId: repositoryId || null,
        docPath: docPath ?? null,
        maxIterations: kind === 'UntilDone' || kind === 'Team' ? maxIterations : null,
        // An interval, or clock times and the zone they are read in (docs/refactor/66).
        ...(kind === 'Scheduled' ? scheduleRequest(schedule) : { intervalMinutes: null }),
        builderDeveloperId: kind === 'Team' ? roles.builder : null,
        reviewerDeveloperId: kind === 'Team' ? roles.reviewer : null,
      }),
    onSuccess: (order) => {
      void queryClient.invalidateQueries({ queryKey: ['work-orders'] })
      onSent?.(order)
      onClose()
    },
    onError: (error) =>
      setProblem(error instanceof ApiError && error.code && KNOWN_ERRORS.includes(error.code) ? t(`work.error.${error.code}`) : t('error.generic')),
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setProblem(null)
    if (!instructions.trim()) {
      setProblem(t('work.error.required'))
      return
    }
    send.mutate()
  }

  const kinds: Array<{ kind: WorkOrderKind; icon: JSX.Element }> = [
    { kind: 'Once', icon: <Play size={13} /> },
    { kind: 'UntilDone', icon: <Repeat size={13} /> },
    { kind: 'Scheduled', icon: <CalendarClock size={13} /> },
    { kind: 'Team', icon: <Users size={13} /> },
  ]

  return (
    <Modal title={t(`work.title.${kind}`)} onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <div className="segmented" role="radiogroup" aria-label={t('work.mode')}>
          {kinds.map((option) => (
            <button
              key={option.kind}
              type="button"
              role="radio"
              aria-checked={kind === option.kind}
              className={`segmented__item${kind === option.kind ? ' segmented__item--active' : ''}`}
              onClick={() => setKind(option.kind)}
            >
              {option.icon} {t(`work.kind.${option.kind}`)}
            </button>
          ))}
        </div>
        <p className="small muted" style={{ margin: 0 }}>{t(`work.explain.${kind}`)}</p>

        {docPath && (
          <p className="small" style={{ margin: 0 }}>
            {t('work.about')} <span className="mono">{docPath}</span>
          </p>
        )}

        <label className="field">
          <span className="field__label">{t('work.instructions')}</span>
          <textarea className="input" rows={5} value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder={t('work.instructionsPlaceholder')} autoFocus />
        </label>

        <div className="row row--wrap">
          <label className="field" style={{ flex: 1, minWidth: 200 }}>
            <span className="field__label">{t('work.repository')}</span>
            <select className="select" value={repositoryId} onChange={(e) => setRepositoryId(e.target.value)}>
              <option value="">{t('work.anyAgent')}</option>
              {linked.map((repository) => (
                <option key={repository.id} value={repository.id}>
                  {repository.name}
                  {repository.agentListening ? ` · ${t('repos.listening')}` : ''}
                </option>
              ))}
            </select>
          </label>

          {(kind === 'UntilDone' || kind === 'Team') && (
            <label className="field" style={{ width: 160 }}>
              <span className="field__label">{t('work.maxRounds')}</span>
              <input className="input" type="number" min={2} max={20} value={maxIterations} onChange={(e) => setMaxIterations(Number(e.target.value) || 8)} />
            </label>
          )}

        </div>

        {kind === 'Scheduled' && <SchedulePicker value={schedule} onChange={setSchedule} />}

        {kind === 'Team' && <TeamRolePicker value={roles} onChange={setRoles} />}

        {remoteOff && (
          <div className="notice notice--warn small">
            {t('work.remoteOff')} <Link to="/agents/remote" onClick={onClose}>{t('work.turnOnRemote')}</Link>
          </div>
        )}

        {problem && <ErrorBanner message={problem} />}

        <div className="row row--end">
          <button type="button" className="btn btn--ghost" onClick={onClose}>{t('common.cancel')}</button>
          <button type="submit" className="btn btn--primary" disabled={send.isPending || remoteOff}>
            {t(`work.send.${kind}`)}
          </button>
        </div>
      </form>
    </Modal>
  )
}
